import React from 'react';

/**
 * A scale weight (ring handle over a splayed block), for the stats panel's
 * weights toggle. Inline rather than an <img> so it takes `currentColor` and
 * needs no second asset for the light theme — same reasoning as ScrollIcon.
 *
 * Three things keep it legible at this size, all learned the hard way:
 *
 * 1. It is ONE outline, not a ring stacked on a block. Two overlapping shapes
 *    of the same colour composite their antialiased edges to under full alpha,
 *    which leaves a pale ragged seam where the circle crosses the block.
 * 2. The handle is large and its hole is r=2.6, four device pixels across at
 *    the size this renders. An earlier version used r=1.95 inside a smaller
 *    ring: at 18px that hole survives as two faint pixels and the handle reads
 *    as a blob rather than a ring. Do not shrink it.
 *
 * 3. The two horizontal edges sit on whole device pixels. This renders in an
 *    18px box, so one pixel is 24/18 = 1.3333 units: the block runs y=10.6667
 *    (8px) to y=21.3333 (16px). At y=21.6 the bottom edge covered a fifth of a
 *    pixel row, and that near-empty row read as a stray grey line under the
 *    icon — which is what made this look rough, not the diagonals. Change the
 *    box size in Generator.css and these two want redoing.
 *
 * The sides are straight, and should stay straight. Bowing them to vary the
 * antialiasing down the edge does work, but a weight whose sides curve is a
 * bell; the pixel grid above is the fix that was actually needed.
 *
 * Geometry, if you edit it: ring centred (12, 6.6667) with r=5, block top edge
 * y=10.6667, so the outline rejoins the circle at x = 12 ± 3 (sqrt(5² − 4²)).
 * Move one and the other two have to follow. Everything mirrors about x=12 —
 * keep it that way or the weight leans.
 */
const WeightIcon = ({ className = '' }) => (
    <svg
        className={className}
        viewBox="0 0 24 24"
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        focusable="false"
        aria-hidden="true"
    >
        <path d="M7 10.6667 L9 10.6667 A5 5 0 1 1 15 10.6667 L17 10.6667 L21.065 20.094 A1.2 1.2 0 0 1 20.85 21.3333 L3.15 21.3333 A1.2 1.2 0 0 1 2.935 20.094 Z M12 4.0667 a2.6 2.6 0 1 0 0 5.2 2.6 2.6 0 0 0 0-5.2 Z" />
    </svg>
);

export default WeightIcon;
